import React, { ChangeEventHandler } from 'react';

import clsx from 'clsx';

type ChoiceType = 'radio' | 'checkbox';
type ChoiceInputColor = 'black' | 'blue';

type ChoiceInputProps = {
  id: string;
  dataTestId?: string;
  name: string;
  label: string;
  errorMessage?: string;
  type?: ChoiceType;
  checked?: boolean;
  isDefaultChecked?: boolean;
  isPlainLabel?: boolean;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  errorBorderOnly?: boolean;
  color?: ChoiceInputColor;
  blurOnDisabled?: boolean;
  labelTextSize?: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  helpText?: string;
};

const ChoiceInput = ({
  id,
  dataTestId,
  name,
  label,
  errorMessage = '',
  type = 'radio',
  checked,
  isDefaultChecked,
  isReadOnly = false,
  isDisabled = false,
  isPlainLabel = false,
  blurOnDisabled = true,
  color = 'black',
  labelTextSize = 'text-sm',
  onChange,
  helpText,
}: ChoiceInputProps) => {
  const baseClasses = clsx(
    color === 'black' ? 'text-black focus:ring-black' : 'text-darkBlue focus:darkBlue',
    type === 'checkbox' ? 'rounded w-4 h-4' : '',
  );
  const disabledClasses = clsx(
    blurOnDisabled ? 'text-gray-300' : `${baseClasses}`,
    'cursor-not-allowed',
  );

  const classStack = clsx(isDisabled ? disabledClasses : baseClasses);

  const labelClassStack = clsx(
    isDisabled
      ? blurOnDisabled
        ? '!cursor-not-allowed text-gray-300'
        : '!cursor-not-allowed'
      : 'cursor-pointer',
    isPlainLabel ? null : 'font-medium',
    'block ml-3',
    labelTextSize,
    checked ? 'font-medium text-darkBlue text-base' : 'text-darkGray',
  );

  const controlledInputProps = {
    checked: checked,
    onChange: onChange,
  };

  const uncontrolledInputProps = {
    defaultChecked: isDefaultChecked,
  };

  const inputProps = isDefaultChecked ? uncontrolledInputProps : controlledInputProps;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center">
        <input
          id={id}
          data-testid={dataTestId}
          name={name}
          type={type}
          readOnly={isReadOnly}
          disabled={isDisabled}
          className={classStack}
          {...inputProps}
        />
        <label htmlFor={id} className={labelClassStack}>
          {label}
        </label>
      </div>
      {helpText ? <span className="text-sm text-gray-500">{helpText}</span> : null}
      {errorMessage ? <p className="text-red mt-2">{errorMessage}</p> : null}
    </div>
  );
};

export default ChoiceInput;
